// Shared listing shortlist — family/friends view saved listings and react.
// Route: /share/{id}?token={viewer_token_or_owner_token}
// No account required. Name entered once, stored in sessionStorage.

import { useEffect, useRef, useState } from "react";

const shortlistId = window.location.pathname.split("/")[2] || "";
const urlToken    = new URLSearchParams(window.location.search).get("token") || "";

const fmtPrice = (n) => n == null ? "—" : `$${Math.round(n).toLocaleString()}`;

const REACTIONS = [
  { id: "love",  emoji: "❤️",  label: "Love it",  color: "#c0354a" },
  { id: "like",  emoji: "👍",  label: "Like",      color: "#2d7a62" },
  { id: "maybe", emoji: "🤔",  label: "Maybe",     color: "#c47c1a" },
  { id: "pass",  emoji: "✗",   label: "Pass",      color: "#9aa9a0" },
];

const CSS = `
  *{box-sizing:border-box}
  body{margin:0;font-family:'Inter','Outfit',sans-serif;background:#f2f8f5;color:#1c2b25}
  .sh-wrap{min-height:100vh;display:flex;flex-direction:column}
  .sh-top{background:#fff;border-bottom:1px solid rgba(45,122,98,0.15);padding:12px 20px;display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .sh-brand{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:#2b8259;letter-spacing:0.2em;cursor:pointer}
  .sh-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:#1c2b25}
  .sh-count{font-family:'DM Mono',monospace;font-size:11px;color:#9bbfb2;letter-spacing:0.06em}
  .sh-body{flex:1;max-width:900px;width:100%;margin:0 auto;padding:24px 16px 60px}
  .sh-name-bar{background:#fff;border:1px solid #d8ede6;border-radius:12px;padding:14px 18px;margin-bottom:24px;display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .sh-name-label{font-size:13px;color:#5a8a78;flex-shrink:0}
  .sh-name-input{flex:1;min-width:160px;background:#f7faf8;border:1px solid #d8ede6;color:#1c2b25;border-radius:8px;padding:7px 12px;font-size:13px;font-family:inherit;outline:none}
  .sh-name-input:focus{border-color:#2d7a62}
  .sh-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
  .sh-card{background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,0.06);border:1.5px solid transparent;transition:border-color 0.15s}
  .sh-card.has-love{border-color:#c0354a}
  .sh-card.has-like{border-color:#2d7a62}
  .sh-card.has-maybe{border-color:#c47c1a}
  .sh-card-img{width:100%;height:160px;object-fit:cover;display:block;background:#e8f0eb}
  .sh-card-img-ph{width:100%;height:160px;background:#e8f0eb;display:flex;align-items:center;justify-content:center;font-size:28px;color:#9bbfb2}
  .sh-card-body{padding:14px 16px 12px}
  .sh-card-price{font-family:'Playfair Display',serif;font-size:20px;font-weight:700;color:#1c2b25;margin:0 0 4px}
  .sh-card-stats{font-size:11px;color:#5a8a78;font-family:'DM Mono',monospace;margin-bottom:4px;display:flex;gap:8px;flex-wrap:wrap}
  .sh-card-addr{font-size:12px;color:#1c2b25;margin-bottom:12px}
  .sh-reactions{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
  .sh-r-btn{display:flex;align-items:center;gap:4px;border:1.5px solid #d8ede6;border-radius:8px;padding:5px 10px;font-size:12px;cursor:pointer;background:#fff;transition:all 0.12s;font-family:inherit}
  .sh-r-btn:hover{background:#f2f8f5}
  .sh-r-btn.active{border-color:currentColor;background:#f2f8f5;font-weight:600}
  .sh-votes{display:flex;flex-wrap:wrap;gap:6px;margin-top:4px}
  .sh-vote-chip{font-size:10px;font-family:'DM Mono',monospace;padding:2px 8px;border-radius:8px;background:#f2f8f5;color:#5a8a78}
  .sh-note-input{width:100%;border:1px solid #d8ede6;border-radius:6px;padding:5px 8px;font-size:11px;font-family:inherit;color:#1c2b25;background:#f7faf8;resize:none;outline:none;margin-top:6px}
  .sh-note-input:focus{border-color:#2d7a62}
  .sh-empty{text-align:center;padding:60px 20px;color:#9bbfb2}
  .sh-err{text-align:center;padding:60px 20px;color:#c0354a;font-size:14px}
`;

function ListingCard({ listing, myName, myReaction, allReactions, onReact }) {
  const [noteMode, setNoteMode] = useState(false);
  const [note, setNote] = useState(myReaction?.note || "");
  const myBorder = myReaction ? `has-${myReaction.reaction}` : "";

  const react = (id) => {
    const isToggle = myReaction?.reaction === id;
    onReact(listing.id, isToggle ? null : id, note);
    if (isToggle) setNoteMode(false);
  };

  return (
    <div className={`sh-card ${myBorder}`}>
      {listing.photo
        ? <img className="sh-card-img" src={listing.photo} alt={listing.address} onError={(e) => e.target.style.display = "none"} />
        : <div className="sh-card-img-ph">{listing.beds || "?"}BR</div>}
      <div className="sh-card-body">
        <div className="sh-card-price">{fmtPrice(listing.price)}</div>
        <div className="sh-card-stats">
          {listing.beds != null && <span><strong>{listing.beds}</strong> bd</span>}
          {listing.baths != null && <span><strong>{listing.baths}</strong> ba</span>}
          {listing.sqft && <span><strong>{listing.sqft.toLocaleString()}</strong> sqft</span>}
          {listing.year_built && <span>built {listing.year_built}</span>}
        </div>
        <div className="sh-card-addr">{listing.address}{listing.city ? `, ${listing.city}` : ""}</div>

        <div className="sh-reactions">
          {REACTIONS.map((r) => {
            const isActive = myReaction?.reaction === r.id;
            return (
              <button
                key={r.id}
                className={`sh-r-btn${isActive ? " active" : ""}`}
                style={{ color: isActive ? r.color : "#5a8a78" }}
                onClick={() => { react(r.id); if (!isActive) setNoteMode(true); }}
                title={r.label}
              >
                <span>{r.emoji}</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10 }}>{r.label}</span>
              </button>
            );
          })}
        </div>

        {noteMode && myReaction && (
          <textarea
            className="sh-note-input"
            rows={2}
            placeholder="Add a note (optional)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => { onReact(listing.id, myReaction.reaction, note); setNoteMode(false); }}
            autoFocus
          />
        )}

        {allReactions?.length > 0 && (
          <div className="sh-votes">
            {allReactions.map((rv, i) => {
              const r = REACTIONS.find((x) => x.id === rv.reaction);
              return (
                <span key={i} className="sh-vote-chip" style={{ color: r?.color || "#5a8a78" }}>
                  {r?.emoji} {rv.name}{rv.note ? ` · "${rv.note}"` : ""}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShareApp() {
  const [shortlist, setShortlist] = useState(null);
  const [err, setErr] = useState(null);
  const [myName, setMyName] = useState(
    () => sessionStorage.getItem("sz_share_name") || ""
  );
  const nameRef = useRef(null);

  useEffect(() => {
    if (!shortlistId || !urlToken) { setErr("Invalid link."); return; }
    fetch(`/api/listings?shortlist=1&id=${shortlistId}&token=${encodeURIComponent(urlToken)}`)
      .then((r) => r.json())
      .then((d) => { if (d.error) setErr(d.error); else setShortlist(d.shortlist); })
      .catch((e) => setErr(e.message));
  }, []);

  const saveName = (n) => {
    setMyName(n);
    sessionStorage.setItem("sz_share_name", n);
  };

  const handleReact = async (listingId, reaction, note) => {
    if (!myName.trim()) { nameRef.current?.focus(); return; }
    if (!reaction) {
      // Remove reaction locally.
      setShortlist((s) => {
        const reactions = { ...s.reactions };
        reactions[listingId] = (reactions[listingId] || []).filter((r) => r.name !== myName);
        return { ...s, reactions };
      });
      return;
    }
    setShortlist((s) => {
      const reactions = { ...s.reactions };
      const list = (reactions[listingId] || []).filter((r) => r.name !== myName);
      reactions[listingId] = [...list, { name: myName, reaction, note: note || "", at: new Date().toISOString() }];
      return { ...s, reactions };
    });
    fetch("/api/listings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "shortlist_react", id: shortlistId, token: urlToken, listing_id: listingId, name: myName, reaction, note }),
    }).catch(() => {});
  };

  if (err) return <><style>{CSS}</style><div className="sh-wrap"><div className="sh-err">{err}</div></div></>;
  if (!shortlist) return <><style>{CSS}</style><div className="sh-wrap"><div className="sh-empty">Loading…</div></div></>;

  const listings = shortlist.listings || [];
  const reactions = shortlist.reactions || {};

  return (
    <>
      <style>{CSS}</style>
      <div className="sh-wrap">
        <header className="sh-top">
          <span className="sh-brand" onClick={() => window.location.href = "/"}>6zero.ai</span>
          <span className="sh-title">{shortlist.name}</span>
          <span className="sh-count">{listings.length} home{listings.length !== 1 ? "s" : ""}</span>
        </header>

        <div className="sh-body">
          <div className="sh-name-bar">
            <span className="sh-name-label">You are:</span>
            <input
              ref={nameRef}
              className="sh-name-input"
              placeholder="Enter your name to react…"
              value={myName}
              onChange={(e) => saveName(e.target.value)}
              maxLength={60}
            />
            {myName && <span style={{ fontSize: 12, color: "#9bbfb2" }}>React to any listing below ↓</span>}
          </div>

          {listings.length === 0
            ? <div className="sh-empty">No listings in this shortlist yet.</div>
            : <div className="sh-grid">
                {listings.map((l) => {
                  const listingReactions = reactions[l.id] || [];
                  const myR = listingReactions.find((r) => r.name === myName) || null;
                  return (
                    <ListingCard
                      key={l.id}
                      listing={l}
                      myName={myName}
                      myReaction={myR}
                      allReactions={listingReactions}
                      onReact={handleReact}
                    />
                  );
                })}
              </div>}
        </div>
      </div>
    </>
  );
}
